import React from "react";
import { Box, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import CountUp from "react-countup";
import { Crown, Trophy } from "lucide-react";
import UserAvatar from "../common/UserAvatar";

const PLACE_RING = ["hsl(40, 80%, 55%)", "hsl(210, 12%, 65%)", "hsl(28, 60%, 55%)"];
const PLACE_INK = ["hsl(40, 68%, 42%)", "hsl(210, 8%, 48%)", "hsl(28, 52%, 44%)"];
const PLACE_BG = ["hsl(44, 86%, 93%)", "hsl(210, 16%, 94%)", "hsl(28, 55%, 93%)"];
const HEIGHTS = { 1: 188, 2: 150, 3: 116 };

// Bars rise in podium order: bronze (#3) -> silver (#2) -> gold (#1).
const ORDER_DELAY = { 3: 0.1, 2: 0.35, 1: 0.6 };
const GROW = 0.7;
const EASE = [0.16, 1, 0.3, 1];

// Resting position of the stats inside each bar (gold highest, bronze lowest).
// `rise` starts the text down at the shared bottom level, then it lifts into place.
const TEXT = {
  1: { justify: "flex-start", pt: "24px", pb: 0, rise: 67 },
  2: { justify: "center", pt: 0, pb: 0, rise: 19 },
  3: { justify: "flex-end", pt: 0, pb: "16px", rise: 0 },
};

const Pillar = ({ entry, place, reduce }) => {
  const top = place === 1;
  const delay = ORDER_DELAY[place];
  const landed = delay + GROW;
  const tcfg = TEXT[place];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
      {top && (
        <Box
          component={motion.div}
          initial={{ scale: reduce ? 1 : 0, y: reduce ? 0 : -8, opacity: reduce ? 1 : 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 14, delay: landed + 0.15 }}
          sx={{ mb: "6px", lineHeight: 0 }}
        >
          <Crown size={20} color="hsl(40, 78%, 50%)" />
        </Box>
      )}

      <Box
        component={motion.div}
        initial={{ y: reduce ? 0 : 50 }}
        animate={{ y: 0 }}
        transition={reduce ? { duration: 0 } : { delay, duration: GROW, ease: EASE }}
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <UserAvatar user={entry.internal_user} size={top ? 62 : 50} ring={PLACE_RING[place - 1]} tooltip={false} />
        <Typography sx={{ fontSize: top ? 15 : 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150, 30%, 12%)", mt: "10px", textAlign: "center" }}>
          {entry.internal_user.full_name}
        </Typography>
      </Box>

      <Box sx={{ position: "relative", mt: "14px", width: "100%", height: HEIGHTS[place] }}>
        <Box
          component={motion.div}
          initial={{ height: reduce ? HEIGHTS[place] : 0 }}
          animate={{ height: HEIGHTS[place] }}
          transition={reduce ? { duration: 0 } : { delay, duration: GROW, ease: EASE }}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "12px 12px 0 0",
            background: `linear-gradient(180deg, ${PLACE_BG[place - 1]}, #fff)`,
            border: "1px solid hsl(140, 8%, 88%)",
            borderBottom: "none",
            boxShadow: top ? "0 10px 24px hsla(40, 65%, 55%, 0.22)" : "0 6px 16px rgba(0,0,0,0.05)",
          }}
        />
        <Box
          component={motion.div}
          initial={{ y: reduce ? 0 : tcfg.rise }}
          animate={{ y: 0 }}
          transition={reduce ? { duration: 0 } : { delay, duration: GROW, ease: EASE }}
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: tcfg.justify,
            pt: tcfg.pt,
            pb: tcfg.pb,
            boxSizing: "border-box",
          }}
        >
          <Typography sx={{ fontSize: 22, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: PLACE_INK[place - 1], lineHeight: 1 }}>
            #{place}
          </Typography>
          <Typography sx={{ mt: "8px", fontSize: 30, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150, 30%, 12%)", lineHeight: 1, letterSpacing: "-0.6px" }}>
            <CountUp end={entry.candidates_hired} duration={reduce ? 0 : GROW} delay={reduce ? 0 : delay} separator="," />
          </Typography>
          <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150, 10%, 46%)", mt: "5px" }}>
            candidates hired
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const Podium = ({ entries = [] }) => {
  const reduce = useReducedMotion();
  const [first, second, third] = entries;

  return (
    <Box
      sx={{
        border: "1px solid hsl(140, 8%, 88%)",
        borderRadius: "8px",
        p: "28px",
        background: "linear-gradient(180deg, hsl(140, 14%, 97%), #fff)",
      }}
    >
      <Box sx={{ textAlign: "center", mb: "26px" }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "7px", color: "hsl(40, 68%, 44%)" }}>
          <Trophy size={14} color="hsl(40, 72%, 48%)" />
          <Typography component="span" sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", letterSpacing: "0.8px", textTransform: "uppercase" }}>
            Hall of Fame
          </Typography>
        </Box>
        <Typography sx={{ m: "8px 0 4px", fontSize: 26, fontFamily: "Segoe UI Bold, sans-serif, system-ui", letterSpacing: "-0.5px", color: "hsl(150, 30%, 12%)" }}>
          Squarehub Legends
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "16px", maxWidth: 640, mx: "auto" }}>
        {second && <Pillar entry={second} place={2} reduce={reduce} />}
        {first && <Pillar entry={first} place={1} reduce={reduce} />}
        {third && <Pillar entry={third} place={3} reduce={reduce} />}
      </Box>
    </Box>
  );
};

export default Podium;
